import { engelMetni, mesafeDoldurmaEngelleri, mmMetni, satirAdi, satirEtiketi, type KesimEngeli, type KesimPlaniTaslagi } from "@oasis/domain";
import { Buton, Hata, Modal, etiket, girdi } from "@/components/parcalar";
import { onayliTaslak, sonradanGirildiMetni, type MesafeDamgasi, type OnayliPlan } from "@/components/kesim-plani-penceresi";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { tarih, tarihSaat } from "@/lib/veri";

/**
 * D-070: kesim mesafesinin onaydan sonra girilmesi (komponent sayfası, "Welds of this item").
 *
 * Kesim planında "No distance record available" ile mesafesiz onaylanan satırın (Pipe to pipe butt kesim ya da ek kaynağı) box ucundan
 * mesafesi sonradan girilir; planın başka hiçbir yeri değişmez, kaynak numaraları onaylandığı gibi kalır. Burada üç parça var:
 * tablonun Position hücresi (`KaynakKonumHucresi`), giriş penceresi (`MesafeGirPenceresi`) ve sonradan girilmiş mesafeyi "no distance
 * record" hâline geri alan pencere (`MesafeGeriPenceresi`). Parçalar denetimlidir: pencere durumu (yazılan değer, meşgul, hata) ve
 * sunucu çağrısı Komponent.tsx'tedir (yanıt iş sayfasının tamamıdır, sayfa yeniden çizilir).
 * Yetki: kesim planını onaylayan (`kesimPlani`: kalite lideri; PM ve sistem yöneticisi her zaman); öteki rollerde düğmeler pasiftir ve
 * ipucunda yetki yazar. Triyaja geri gönderilmiş (salt okunur) işte düğmeler çizilmez.
 */

/**
 * Sunucunun gövde sınırı: `kesim-mesafesi` ucu 100 000 mm'nin üstünü gövde şemasında reddeder (plan gövdesindeki her mm değerinin
 * sınırı; apps/api planlama.controller.ts `KesimMesafesiDto`). O ret bir cümle değildir ("Validation failed"), bu yüzden istek hiç
 * gönderilmez. Boyu olan planda bu sınıra gelmeden aralık engeli (cümlesiyle) doğar; sınır yalnız boyu boş planda işe yarar.
 */
export const MESAFE_UST_SINIR_MM = 100_000;

/**
 * Giriş kutusuna yazılan metnin denetimi (saf; sunucuyla aynı alan kuralı). Boş metin geçersizdir ama hata değildir; tam sayı olmayan
 * değer "Enter a whole number of millimetres." hatasıdır; tam sayıda alan paketinin `mesafeDoldurmaEngelleri` kuralı çalışır (0 ile
 * boru boyu arası, başka satırla aynı mesafe değil). `mm` yalnız tam sayı yazılmışsa doludur. Gövde sınırının üstündeki değer engel
 * cümlesi doğurmaz ama geçerli de sayılmaz (kaydet düğmesi pasif kalır).
 */
export function mesafeGirdisi(taslak: KesimPlaniTaslagi, sira: number, metin: string): { mm: number | null; tamSayi: boolean; engeller: KesimEngeli[]; gecerli: boolean } {
  const t = metin.trim();
  if (t === "") return { mm: null, tamSayi: true, engeller: [], gecerli: false };
  const n = Number(t);
  if (!Number.isInteger(n)) return { mm: null, tamSayi: false, engeller: [], gecerli: false };
  const engeller = mesafeDoldurmaEngelleri(taslak, sira, n);
  return { mm: n, tamSayi: true, engeller, gecerli: engeller.length === 0 && n <= MESAFE_UST_SINIR_MM };
}

/**
 * "Welds of this item" tablosunun Position hücresi. `yer`: kaynağın bugünkü konum yazısı ("8,000" · "630 · at box flange weld" · "—").
 * `kayitYok`: kaynak beyanlı satırdan doğmuş ve mesafesi hâlâ boş → "no distance record" ve küçük "Enter distance…" düğmesi.
 * `girildi`: mesafe onaydan sonra girilmiş (kesim satırındaki damga) → değer, altında "entered later · kişi · tarih saat" ve küçük
 * "Take back…" düğmesi. Planla birlikte onaylanan mesafede iki düğme de yoktur. `salt`: iş salt okunur (geri gönderilmiş), düğme çizilmez.
 */
export function KaynakKonumHucresi({ yer, kayitYok, girildi, salt, onGir, onGeriAl }: {
  yer: string; kayitYok: boolean; girildi: MesafeDamgasi | null; salt: boolean; onGir: () => void; onGeriAl: () => void;
}) {
  if (kayitYok) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span>{cevir("no distance record")}</span>
        {!salt && <Buton kucuk islem="kesimPlani" className="whitespace-nowrap" onClick={onGir}>{cevir("Enter distance…")}</Buton>}
      </div>
    );
  }
  if (girildi) {
    return (
      <>
        {yer}
        <span className="block text-xs text-ink-3">{sonradanGirildiMetni(girildi)}</span>
        {!salt && <Buton kucuk islem="kesimPlani" className="mt-1 whitespace-nowrap" onClick={onGeriAl}>{cevir("Take back…")}</Buton>}
      </>
    );
  }
  return <>{yer}</>;
}

/**
 * "Enter distance…" penceresi (dar Modal). Kendi hata kutusu vardır (sayfanın hata bandı pencerenin arkasında kalır); hata olunca
 * pencere açık kalır. Kaydet düğmesi yazılan değeri yineler ("Save 8,000 mm"); kutu boşken, değer geçersizken ya da gönderilirken
 * "Save" yazar ve pasiftir. Bu pencerede uyarı (kusur bölgesi, 500 mm yakınlık) verilmez: giriş yapılmış bir kesimin kaydıdır,
 * kesim planlamaz. Satır planda yoksa (sayfa bu arada değişti) hiçbir şey çizilmez.
 */
export function MesafeGirPenceresi({ plan, kesimId, weldNo, deger, mesgul, hata, onDeger, onKaydet, onKapat }: {
  plan: OnayliPlan; kesimId: string; weldNo: number; deger: string; mesgul: boolean; hata: string | null;
  onDeger: (v: string) => void; onKaydet: (mesafeMm: number) => void | Promise<void>; onKapat: () => void;
}) {
  const k = plan.kesimler.find((x) => x.id === kesimId);
  if (!k) return null;
  const taslak = onayliTaslak(plan);
  const boy = taslak.referans.boy;
  const g = mesafeGirdisi(taslak, k.sira, deger);
  const mm = g.gecerli ? g.mm : null;
  return (
    <Modal baslik={cevir`Distance from box end — ${satirAdi(k)} · W${weldNo}`} onKapat={onKapat} kilitli={mesgul}>
      <p className="text-sm leading-relaxed text-ink-2">{cevir`This row was approved with “No distance record available” (${k.mesafeYok?.kim ?? plan.onaylayan} · ${tarih(k.mesafeYok?.tarih ?? plan.onayTs)}). Enter the distance when a record gives it.`}</p>
      <div className="mt-3">
        <label className={etiket}>{cevir("Distance from box end (mm)")}</label>
        <input type="number" inputMode="numeric" aria-label={cevir("Distance from box end (mm)")} className={cn(girdi, "w-44")} value={deger} disabled={mesgul} onChange={(e) => onDeger(e.target.value)} />
        <p className="mt-1 text-xs text-ink-3">{boy !== null ? cevir`0 to ${mmMetni(boy)} mm (tube length), whole millimetres` : cevir("0 or more, whole millimetres")}</p>
        {!g.tamSayi && <p className="mt-1 text-xs text-bad">{cevir("Enter a whole number of millimetres.")}</p>}
        {g.engeller.map((e, i) => <p key={i} className="mt-1 text-xs text-bad">{cevir(engelMetni(e, satirEtiketi(taslak)))}</p>)}
      </div>
      <p className="mt-3 text-xs text-ink-3">{cevir`A saved distance cannot be edited here; it can only be taken back to “no distance record”. The weld number stays W${weldNo}.`}</p>
      <p className="mt-1 text-xs text-ink-3">{cevir("Who can enter: the Quality Lead only (PM and System Administrator always). Your name and the time are recorded.")}</p>
      {hata && <div className="mt-2"><Hata mesaj={hata} /></div>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Buton tur="birincil" islem="kesimPlani" mesgul={mesgul} disabled={mm === null} onClick={() => (mm === null ? undefined : onKaydet(mm))}>
          {mm !== null && !mesgul ? cevir`Save ${mmMetni(mm)} mm` : cevir("Save")}
        </Buton>
        <Buton disabled={mesgul} onClick={onKapat}>{cevir("Cancel")}</Buton>
      </div>
    </Modal>
  );
}

/**
 * "Take back…" penceresi (K4). Sonradan girilmiş mesafeyi "no distance record" hâline geri alır; mesafe yeniden girilebilir. Planla
 * birlikte onaylanan mesafe geri alınamaz (düğmesi yoktur, sunucu da reddeder). Eski değer satırın geçmişinde ve olay kaydında kalır.
 * Satır planda yoksa ya da mesafesi boşsa (sayfa bu arada değişti) hiçbir şey çizilmez.
 * `onGeriAl` pencerenin gösterdiği değeri (mm) alır ve sunucuya gönderir: sayfa bayatsa (başka biri bu arada geri alıp yeni mesafe
 * girmişse) sunucu satırdaki değerle karşılaştırır ve kişinin onaylamadığı değeri geri almaz.
 */
export function MesafeGeriPenceresi({ plan, kesimId, weldNo, mesgul, hata, onGeriAl, onKapat }: {
  plan: OnayliPlan; kesimId: string; weldNo: number; mesgul: boolean; hata: string | null; onGeriAl: (mesafeMm: number) => void | Promise<void>; onKapat: () => void;
}) {
  const k = plan.kesimler.find((x) => x.id === kesimId);
  if (!k || k.mesafeMm === null) return null;
  const gosterilen = k.mesafeMm;
  const mm = mmMetni(gosterilen);
  return (
    <Modal baslik={cevir`Take back distance — ${satirAdi(k)} · W${weldNo}`} onKapat={onKapat} kilitli={mesgul}>
      <p className="text-sm leading-relaxed text-ink-2">{cevir`${mm} mm was entered later by ${k.mesafeGirildi?.kim ?? "—"} · ${tarihSaat(k.mesafeGirildi?.tarih)}. Taking it back returns this row to “no distance record”; the distance can then be entered again. A distance approved with the plan cannot be taken back. Your name and the time are recorded.`}</p>
      {hata && <div className="mt-2"><Hata mesaj={hata} /></div>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Buton tur="tehlike" islem="kesimPlani" mesgul={mesgul} onClick={() => onGeriAl(gosterilen)}>{cevir`Take back ${mm} mm`}</Buton>
        <Buton disabled={mesgul} onClick={onKapat}>{cevir("Cancel")}</Buton>
      </div>
    </Modal>
  );
}
